import React, { useEffect, useMemo } from "react";
import PubSub from "pubsub-js";
import { observer } from "mobx-react-lite";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import "./App.scss";
import RoutesComponent from "./components/routes/RoutesComponent";
import PictureInPictureVideo from "./components/video-player/PictureInPictureVideo";
import LoadingSpinner from "./components/loading-spinner/LoadingSpinner";
import { PubSubEvents } from "./services/PubSubEvents";
import { organizationSubscriptionService } from "./services/organization/OrganizationSubscription";
import { permissionsService } from "./services/authorization/UserPermissionsService";
import { lastAccessedOrganizationService } from "./services/organization/LastAccessedOrganizationStorage";
import { areLoading } from "./services/storage/StorageModel";
import { currentUserService } from "./services/user/CurrentUserStorage";
import { featureFlagsStorage } from "./services/feature-flags/FeatureFlagsStorage";
import { videoPlayerService } from "./services/video-player/VideoPlayerService";
import { translationService } from "./services/translation/TranslationService";
import { mainCodeColorStorage } from "./services/mc-colors/MainCodeColorStorage";

const App: React.FC = observer(() => {
  const { videoUrl, timestamps } = videoPlayerService;

  const redirectedToMyAccountAfterPasswordChange = useMemo(() => {
    return (
      window.location.href.includes("/my-account") &&
      !window.location.href.includes("/my-account/organizations") &&
      !window.location.href.endsWith("/my-account")
    );
  }, []);

  useEffect(() => {
    if (redirectedToMyAccountAfterPasswordChange) {
      window.location.href = "/my-account";
    }
  }, [redirectedToMyAccountAfterPasswordChange]);

  useEffect(() => {
    window.addEventListener("beforeunload", () => {
      PubSub.publish(PubSubEvents.BeforeUnload);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    var featureFlagChangedSubscribeToken = PubSub.subscribe(
      PubSubEvents.FeatureFlagsChanged,
      (event: string, notification: Notification) => {
        console.log("Feature flags changed event received");
        featureFlagsStorage.resetFeatureFlags();
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(featureFlagChangedSubscribeToken);
    };
  }, []);

  if (
    areLoading([
      lastAccessedOrganizationService,
      organizationSubscriptionService,
      permissionsService,
      currentUserService,
      featureFlagsStorage,
      mainCodeColorStorage
    ])
  ) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={translationService.locale}>
      <div className="App">
        <RoutesComponent />
        <PictureInPictureVideo url={videoUrl} attributes={{ timestamps }} />
      </div>
    </LocalizationProvider>
  );
});

export default App;
